import React, { useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Anchor, ArrowLeft, ArrowRight, BarChart3, BookOpen, Building2, Cloud, Code2, FileCode2, Heart, Leaf, MapPin, Network, Package, Palette, Search, ShoppingBag, Sparkles, Squirrel, Users, Workflow, Zap, BoxIcon } from 'lucide-react';
type TabId = 'work' | 'seo';
interface Project {
  name: string;
  category: string;
  description: string;
  icon: BoxIcon;
}
interface SeoCapability {
  title: string;
  description: string;
  icon: BoxIcon;
}
const PROJECTS_PER_PAGE = 6;
const tabs: {
  id: TabId;
  label: string;
}[] = [{
  id: 'work',
  label: 'Latest work'
}, {
  id: 'seo',
  label: 'SEO & visibility'
}];
const selectedProjects: Project[] = [{
  name: 'JYSK UK',
  category: 'Drupal / E-commerce',
  description: 'A commerce experience shaped for a recognised international retail brand.',
  icon: ShoppingBag
}, {
  name: 'Seacat by Rossinavi',
  category: 'React / Luxury Marine',
  description: 'A refined digital presence for an innovative luxury marine experience.',
  icon: Anchor
}, {
  name: 'TransfrInc',
  category: 'Design / Corporate',
  description: 'A corporate platform with a clearer brand expression and focused journey.',
  icon: Palette
}, {
  name: 'MyCPACoach',
  category: 'Design / Education',
  description: 'A clear learning platform that helps specialist content feel accessible and useful.',
  icon: BookOpen
}, {
  name: 'BENS',
  category: 'Custom Plugin / Non-Profit',
  description: 'A tailored digital solution designed to support a mission-led organisation.',
  icon: Heart
}, {
  name: 'SABCap',
  category: 'Corporate / Finance',
  description: 'A structured corporate presence designed for confidence and clarity.',
  icon: Building2
}, {
  name: 'MyCalgary',
  category: 'Community / Portal',
  description: 'A community portal that makes local information easier to access and act on.',
  icon: MapPin
}, {
  name: 'The Human Squirrel',
  category: 'Creative / Web Design',
  description: 'A distinctive web experience with personality built into every touchpoint.',
  icon: Squirrel
}, {
  name: 'WDCACDST',
  category: 'Non-Profit / Organisation',
  description: 'A focused digital home for a community organisation and its audience.',
  icon: Users
}, {
  name: 'EcoMinim',
  category: 'E-commerce / Sustainable',
  description: 'A considered retail experience for a sustainability-focused brand.',
  icon: Leaf
}, {
  name: 'Little Brand Box',
  category: 'E-commerce / Subscription Retail',
  description: 'A subscription commerce experience that makes recurring discovery feel simple.',
  icon: Package
}, {
  name: 'Hair Health',
  category: 'E-commerce / Health & Beauty',
  description: 'A customer-first store experience for a specialist wellness brand.',
  icon: Sparkles
}, {
  name: 'Africa Switchgear',
  category: 'Industrial / Manufacturing',
  description: 'A practical corporate platform for a technical manufacturing business.',
  icon: Zap
}, {
  name: 'TerraHub Marketplace',
  category: 'E-commerce / Sustainable Marketplace',
  description: 'A marketplace experience connecting sustainability-led products and people.',
  icon: Leaf
}, {
  name: 'Global Parenting Initiative',
  category: 'Non-Profit / Education',
  description: 'An approachable education platform supporting families and communities.',
  icon: Heart
}, {
  name: 'Cloudit',
  category: 'Technology / IT Services',
  description: 'A sharper technology presence built to explain complex services with ease.',
  icon: Cloud
}, {
  name: 'Maiden Cricket',
  category: 'Shopify / E-commerce',
  description: 'A considered online storefront built around product discovery and conversion.',
  icon: ShoppingBag
}, {
  name: "Kelly's",
  category: 'E-commerce / Retail',
  description: 'A retail experience that balances brand character with a clear path to purchase.',
  icon: ShoppingBag
}];
const seoCapabilities: SeoCapability[] = [{
  title: 'Technical foundations',
  description: 'Technical SEO enhancements that address crawlability, indexing and the performance signals search engines depend on.',
  icon: Search
}, {
  title: 'Site structure',
  description: 'Clearer information architecture, internal linking and page relationships that help people and search engines find priority content.',
  icon: Network
}, {
  title: 'Schema & search signals',
  description: 'Structured data implementation and targeted metadata improvements that give search engines better context.',
  icon: FileCode2
}, {
  title: 'CMS & code improvements',
  description: 'Focused CMS and front-end changes that strengthen search visibility while protecting the existing design and functionality.',
  icon: Code2
}, {
  title: 'Search Console management',
  description: 'Google Search Console monitoring and practical issue resolution, with informed next steps for ongoing optimisation.',
  icon: BarChart3
}, {
  title: 'Sustainable visibility',
  description: 'An actionable SEO roadmap that prioritises the changes most likely to improve discoverability over time.',
  icon: Workflow
}];
export function SelectedWorkSection() {
  const [activeTab, setActiveTab] = useState<TabId>('work');
  const [workPage, setWorkPage] = useState(0);
  const tabRefs = useRef<Record<TabId, HTMLButtonElement | null>>({
    work: null,
    seo: null
  });
  const workPanelRef = useRef<HTMLDivElement>(null);
  const totalWorkPages = Math.ceil(selectedProjects.length / PROJECTS_PER_PAGE);
  const pagedProjects = selectedProjects.slice(workPage * PROJECTS_PER_PAGE, (workPage + 1) * PROJECTS_PER_PAGE);
  const selectTab = (tab: TabId) => {
    setActiveTab(tab);
  };
  const changeWorkPage = (nextPage: number) => {
    setWorkPage(nextPage);
    window.setTimeout(() => workPanelRef.current?.focus(), 0);
  };
  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, currentTab: TabId) => {
    const currentIndex = tabs.findIndex(tab => tab.id === currentTab);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    const nextTab = tabs[nextIndex].id;
    selectTab(nextTab);
    tabRefs.current[nextTab]?.focus();
  };
  return <section id="selected-work" className="bg-dark-lighter py-24" aria-labelledby="selected-work-heading" data-id="element-253">
      <div className="mx-auto max-w-[1320px] px-4" data-id="element-254">
        <div className="flex flex-col justify-between gap-7 md:flex-row md:items-end" data-id="element-255">
          <div className="max-w-2xl" data-id="element-256">
            <motion.p initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="mb-3 font-inter text-xs font-bold uppercase tracking-[0.2em] text-gold" data-id="element-257">
              What we make better
            </motion.p>
            <motion.h2 id="selected-work-heading" initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.06
          }} className="font-syne text-4xl font-bold leading-tight text-white md:text-5xl" data-id="element-258">
              Work that performs beyond the launch.
            </motion.h2>
          </div>
          <motion.p initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.12
        }} className="max-w-md font-inter leading-relaxed text-white/60" data-id="element-259">
            Explore a selection of digital work, or see how Byteweb helps
            existing websites become easier to find, understand and grow.
          </motion.p>
        </div>

        <div className="mt-10 inline-flex w-full gap-1 rounded-xl border border-white/10 bg-dark p-1 sm:w-auto" role="tablist" aria-label="Selected work and SEO services" data-id="element-260">
          {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          return <button key={tab.id} ref={element => {
            tabRefs.current[tab.id] = element;
          }} type="button" role="tab" id={`${tab.id}-tab`} aria-selected={isActive} aria-controls={`${tab.id}-panel`} tabIndex={isActive ? 0 : -1} onClick={() => selectTab(tab.id)} onKeyDown={event => handleTabKeyDown(event, tab.id)} className={`relative flex-1 rounded-lg px-4 py-3 font-inter text-sm font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-gold sm:flex-none sm:px-6 ${isActive ? 'bg-gold text-true-white' : 'text-white/60 hover:bg-white/5 hover:text-white'}`} data-id="element-261">
                {tab.label}
              </button>;
        })}
        </div>

        <AnimatePresence mode="wait" data-id="element-262">
          {activeTab === 'work' ? <motion.div key={`work-${workPage}`} id="work-panel" role="tabpanel" aria-labelledby="work-tab" tabIndex={-1} ref={workPanelRef} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.28
        }} className="mt-8 outline-none focus:ring-2 focus:ring-gold" data-id="element-263">
              <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-264">
                <p className="font-inter text-sm text-white/50" data-id="element-265">
                  Showing {workPage * PROJECTS_PER_PAGE + 1}–
                  {Math.min((workPage + 1) * PROJECTS_PER_PAGE, selectedProjects.length)}{' '}
                  of {selectedProjects.length} projects
                </p>
                <p className="font-inter text-xs font-bold uppercase tracking-[0.16em] text-gold" data-id="element-266">
                  Page {workPage + 1} of {totalWorkPages}
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2" data-id="element-267">
                {pagedProjects.map((project, index) => {
              const Icon = project.icon;
              const projectNumber = workPage * PROJECTS_PER_PAGE + index + 1;
              return <motion.article key={project.name} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.06
              }} className="relative overflow-hidden rounded-2xl border border-white/10 bg-dark p-7 sm:p-8" data-id="element-268">
                      <div className="flex items-start justify-between gap-6" data-id="element-269">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-gold/25 bg-gold/10 text-gold" data-id="element-270">
                          <Icon size={22} data-id="element-271" />
                        </div>
                        <span className="font-inter text-xs font-bold uppercase tracking-[0.14em] text-white/35" data-id="element-272">
                          {String(projectNumber).padStart(2, '0')}
                        </span>
                      </div>
                      <p className="mt-12 font-inter text-xs font-bold uppercase tracking-[0.14em] text-gold" data-id="element-273">
                        {project.category}
                      </p>
                      <h3 className="mt-3 font-syne text-2xl font-bold text-white" data-id="element-274">
                        {project.name}
                      </h3>
                      <p className="mt-3 max-w-md font-inter leading-relaxed text-white/55" data-id="element-275">
                        {project.description}
                      </p>
                    </motion.article>;
            })}
              </div>

              <nav className="mt-8 flex flex-col gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between" aria-label="Project pagination" data-id="element-276">
                <button type="button" onClick={() => changeWorkPage(workPage - 1)} disabled={workPage === 0} className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 px-4 py-2.5 font-inter text-sm font-bold text-white transition-colors hover:border-gold hover:text-gold disabled:cursor-not-allowed disabled:opacity-35 focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-277">
                  <ArrowLeft size={16} data-id="element-278" />
                  Previous
                </button>

                <div className="flex items-center justify-center gap-2" data-id="element-279">
                  {Array.from({
                length: totalWorkPages
              }, (_, page) => <button key={page} type="button" onClick={() => changeWorkPage(page)} className={`flex h-9 min-w-9 items-center justify-center rounded-full px-2 font-inter text-sm font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-gold ${page === workPage ? 'bg-gold text-true-white' : 'text-white/55 hover:bg-white/10 hover:text-white'}`} aria-label={`Show project page ${page + 1}`} aria-current={page === workPage ? 'page' : undefined} data-id="element-280">
                        {page + 1}
                      </button>)}
                </div>

                <button type="button" onClick={() => changeWorkPage(workPage + 1)} disabled={workPage === totalWorkPages - 1} className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 px-4 py-2.5 font-inter text-sm font-bold text-white transition-colors hover:border-gold hover:text-gold disabled:cursor-not-allowed disabled:opacity-35 focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-281">
                  Next
                  <ArrowRight size={16} data-id="element-282" />
                </button>
              </nav>

              <div className="mt-8 flex justify-end" data-id="element-283">
                <a href="#contact" className="group inline-flex items-center gap-2 font-inter text-sm font-bold text-gold transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-284">
                  Discuss your next digital product
                  <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" data-id="element-285" />
                </a>
              </div>
            </motion.div> : <motion.div key="seo" id="seo-panel" role="tabpanel" aria-labelledby="seo-tab" tabIndex={0} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.28
        }} className="mt-8 outline-none focus:ring-2 focus:ring-gold" data-id="element-286">
              <div className="grid gap-8 rounded-2xl border border-white/10 bg-dark p-7 lg:grid-cols-[0.9fr_1.1fr] lg:p-10" data-id="element-287">
                <div data-id="element-288">
                  <p className="font-inter text-xs font-bold uppercase tracking-[0.2em] text-gold" data-id="element-289">
                    Technical SEO
                  </p>
                  <h3 className="mt-3 font-syne text-3xl font-bold leading-tight text-white md:text-4xl" data-id="element-290">
                    Make your existing website easier to discover.
                  </h3>
                  <p className="mt-5 max-w-xl font-inter leading-relaxed text-white/65" data-id="element-291">
                    Search visibility is often limited by the technical details
                    behind an otherwise strong site. We identify the practical
                    improvements that help search engines crawl, index and
                    understand your content—without compromising the experience
                    your customers already value.
                  </p>
                  <a href="#contact" className="mt-8 inline-flex items-center gap-2 rounded-lg bg-gold px-5 py-3 font-inter text-sm font-bold text-true-white transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-292">
                    Discuss an SEO audit
                    <ArrowRight size={17} data-id="element-293" />
                  </a>
                </div>

                <div className="grid gap-3 sm:grid-cols-2" data-id="element-294">
                  {seoCapabilities.map((capability, index) => {
                const Icon = capability.icon;
                return <motion.article key={capability.title} initial={{
                  opacity: 0,
                  y: 16
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: index * 0.05
                }} className="rounded-xl border border-white/10 bg-dark-lighter p-5" data-id="element-295">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold/10 text-gold" data-id="element-296">
                          <Icon size={19} data-id="element-297" />
                        </div>
                        <h4 className="mt-5 font-syne text-lg font-bold text-white" data-id="element-298">
                          {capability.title}
                        </h4>
                        <p className="mt-2 font-inter text-sm leading-relaxed text-white/55" data-id="element-299">
                          {capability.description}
                        </p>
                      </motion.article>;
              })}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}